import React, { useState, useEffect, lazy, Suspense } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SystemSettingsProvider } from './contexts/SystemSettingsContext';
import Auth from './components/Auth';
import Homepage from './components/Homepage';
import FirstLoginAgreementModal from './components/FirstLoginAgreementModal';
import ErrorBoundary from './components/ErrorBoundary';
import { supabase } from './lib/supabase';

// Lazy load heavy components
const Layout = lazy(() => import('./components/Layout'));
const PlaylistManager = lazy(() => import('./components/PlaylistManager'));
const MusicPlayer = lazy(() => import('./components/MusicPlayer'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const AdsManager = lazy(() => import('./components/AdsManager'));
const MusicDiscovery = lazy(() => import('./components/MusicDiscovery'));
const PlaylistScheduleManager = lazy(() => import('./components/PlaylistScheduleManager'));
const PricingManager = lazy(() => import('./components/PricingManager'));
const UserSettings = lazy(() => import('./components/UserSettings'));

type View = 'homepage' | 'auth' | 'pricing' | 'app';

const AppContent: React.FC = () => {
  const { user, loading, profile, signOut, isLocked } = useAuth();
  const [currentView, setCurrentViewRaw] = useState<View>(() => {
    const saved = sessionStorage.getItem('currentView');
    return (saved === 'app' || saved === 'auth' || saved === 'pricing') ? saved as View : 'homepage';
  });
  const [currentPage, setCurrentPageRaw] = useState(() => {
    return sessionStorage.getItem('currentPage') || 'playlists';
  });
  const [selectedPlaylistId, setSelectedPlaylistId] = useState<string | null>(() => {
    return sessionStorage.getItem('selectedPlaylistId') || null;
  });
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [needsLegalAgreement, setNeedsLegalAgreement] = useState(false);
  const [currentLegalVersion, setCurrentLegalVersion] = useState<number>(1);
  const [checkoutMessage, setCheckoutMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const pageTitles: Record<string, string> = {
      playlists: 'Playlists',
      player: 'Music Player',
      schedules: 'Schedules',
      discover: 'Discover Music',
      ads: 'Ads Manager',
      settings: 'Settings',
      admin: 'Admin Panel',
    };
    const base = 'PlexyMusic';
    if (currentView === 'homepage') {
      document.title = `${base} - AI-Powered Background Music Platform for Businesses`;
    } else if (currentView === 'auth') {
      document.title = `Sign In | ${base}`;
    } else if (currentView === 'pricing') {
      document.title = `Pricing | ${base}`;
    } else if (currentView === 'app') {
      const pageTitle = pageTitles[currentPage] || 'Dashboard';
      document.title = `${pageTitle} | ${base}`;
    }
  }, [currentView, currentPage]);

  const setCurrentView = (view: View) => {
    setCurrentViewRaw(view);
    sessionStorage.setItem('currentView', view);
  };

  const setCurrentPage = (page: string) => {
    setCurrentPageRaw(page);
    sessionStorage.setItem('currentPage', page);
  };

  // Handle checkout redirect callbacks
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const checkoutStatus = urlParams.get('checkout');

    if (checkoutStatus === 'success') {
      setCheckoutMessage({ type: 'success', text: 'Payment successful! Your subscription is now active.' });
      setSelectedPackageId(null);
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => setCheckoutMessage(null), 5000);
    } else if (checkoutStatus === 'cancel') {
      setCheckoutMessage({ type: 'error', text: 'Payment cancelled. You can try again from the Settings page.' });
      setSelectedPackageId(null);
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => setCheckoutMessage(null), 5000);
    }
  }, []);

  // Check if user needs to accept legal agreement
  useEffect(() => {
    const checkLegalAgreement = async () => {
      if (!user || !profile) return;

      try {
        const { data, error } = await supabase
          .from('legal_documents')
          .select('version')
          .eq('document_type', 'full_agreement')
          .eq('is_active', true)
          .maybeSingle();

        if (error) throw error;

        const currentVersion = data?.version || 1;
        setCurrentLegalVersion(currentVersion);

        const userAcceptedVersion = profile.legal_agreement_version_accepted;

        if (!userAcceptedVersion || userAcceptedVersion < currentVersion || profile.forceAgreementAcceptance) {
          setNeedsLegalAgreement(true);
        } else {
          setNeedsLegalAgreement(false);
        }
      } catch (err: any) {
        console.error('Error checking legal agreement:', err);
      }
    };

    checkLegalAgreement();
  }, [user, profile]);

  // Automatically redirect authenticated users to app
  useEffect(() => {
    if (user && (currentView === 'auth' || currentView === 'pricing')) {
      setCurrentView('app');
      // If user has selected a package, go to settings to trigger payment
      // Otherwise go to playlists
      if (selectedPackageId) {
        setCurrentPage('settings');
      } else {
        setCurrentPage('playlists');
        setSelectedPackageId(null);
      }
    }
  }, [user, currentView, selectedPackageId]);

  // Reset to homepage when user logs out
  useEffect(() => {
    if (!user && !loading && currentView === 'app') {
      setCurrentView('homepage');
      setCurrentPage('playlists');
      setSelectedPlaylistId(null);
      sessionStorage.removeItem('currentView');
      sessionStorage.removeItem('currentPage');
      sessionStorage.removeItem('selectedPlaylistId');
    }
  }, [user, loading, currentView]);

  const handlePlayPlaylist = (playlistId: string) => {
    setSelectedPlaylistId(playlistId);
    sessionStorage.setItem('selectedPlaylistId', playlistId);
    setCurrentPage('player');
  };

  const handleNavigateAway = (newPage: string) => {
    if (currentPage === 'player' && newPage !== 'player') {
      setSelectedPlaylistId(null);
      sessionStorage.removeItem('selectedPlaylistId');
    }
    setCurrentPage(newPage);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <div className="text-white font-medium">Loading PlexyMusic...</div>
        </div>
      </div>
    );
  }

  // Show authenticated app
  if (user && currentView === 'app') {
    // Show legal agreement modal if needed
    if (needsLegalAgreement && profile) {
      return (
        <FirstLoginAgreementModal
          userId={user.id}
          userEmail={user.email || ''}
          userFullName={profile.fullName}
          onAccept={() => setNeedsLegalAgreement(false)}
          onDecline={() => signOut()}
        />
      );
    }

    const effectivePage = isLocked ? 'settings' : currentPage;

    return (
      <Suspense fallback={
        <div className="min-h-screen bg-gray-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
            <div className="text-white font-medium">Loading...</div>
          </div>
        </div>
      }>
        <Layout
          currentPage={effectivePage}
          onNavigate={isLocked ? () => {} : handleNavigateAway}
          onViewHomepage={() => setCurrentView('homepage')}
        >
          {isLocked && (
            <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white py-3 px-4 text-center shadow-lg">
              <p className="font-semibold">
                Your subscription has expired. Please subscribe to a plan to continue using PlexyMusic.
              </p>
            </div>
          )}
          {checkoutMessage && (
            <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg backdrop-blur-sm max-w-md animate-fade-in-down ${
              checkoutMessage.type === 'success'
                ? 'bg-green-500/20 border border-green-500/50 text-green-200'
                : 'bg-red-500/20 border border-red-500/50 text-red-200'
            }`} style={{ top: isLocked ? '60px' : '16px' }}>
              <p className="font-medium">{checkoutMessage.text}</p>
            </div>
          )}
          {!isLocked && effectivePage === 'playlists' && (
            <PlaylistManager onPlayPlaylist={handlePlayPlaylist} onNavigate={handleNavigateAway} />
          )}
          {!isLocked && (
            <div style={{ display: effectivePage === 'player' ? 'block' : 'none' }}>
              <MusicPlayer key={user.id} playlistId={selectedPlaylistId} onPlayPlaylist={handlePlayPlaylist} />
            </div>
          )}
          {!isLocked && effectivePage === 'schedules' && <PlaylistScheduleManager />}
          {!isLocked && effectivePage === 'discover' && <MusicDiscovery />}
          {!isLocked && effectivePage === 'ads' && <AdsManager />}
          {effectivePage === 'settings' && <UserSettings selectedPackageId={selectedPackageId} />}
          {!isLocked && effectivePage === 'admin' && <AdminPanel />}
        </Layout>
      </Suspense>
    );
  }

  // Show homepage for non-authenticated users OR if user is logged in but on homepage view
  if (currentView === 'homepage') {
    return (
      <Homepage
        onNavigateToAuth={(packageId?: string) => {
          setSelectedPackageId(packageId || null);
          if (user) {
            setCurrentView('app');
            setCurrentPage('playlists');
          } else {
            setCurrentView('auth');
          }
        }}
        onNavigateToPricing={() => {
          if (user) {
            setCurrentView('app');
            setCurrentPage('playlists');
          } else {
            setCurrentView('pricing');
          }
        }}
      />
    );
  }

  // Show auth page
  if (currentView === 'auth' || currentView === 'pricing') {
    return (
      <Auth
        onBack={() => {
          setCurrentView('homepage');
          setSelectedPackageId(null);
        }}
        selectedPackageId={selectedPackageId}
      />
    );
  }

  return null;
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-black via-secondary-900 to-black">
        <AuthProvider>
          <SystemSettingsProvider>
            <ErrorBoundary>
              <AppContent />
            </ErrorBoundary>
          </SystemSettingsProvider>
        </AuthProvider>
      </div>
    </ErrorBoundary>
  );
};

export default App;